import React, { useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import ImageUploader from "quill-image-uploader";
import ImageResize from "../../packages/quill-image-resize-module/ImageResize";
import { getAssetLink } from '../../utils/getAssetLink';
import mailClientAPI from '../../api/mail-client-api';
import 'react-quill/dist/quill.snow.css';
import 'quill-image-uploader/dist/quill.imageUploader.min.css';

// Register Quill modules only once (singleton pattern)
let modulesRegistered = false;

const registerQuillModules = () => {
  if (!modulesRegistered) {
    try {
      Quill.register("modules/imageUploader", ImageUploader);
      Quill.register("modules/imageResize", ImageResize);
      modulesRegistered = true;
    } catch (error) {
      // Modules might already be registered, which is fine
      console.warn("Quill modules registration:", error.message);
    }
  }
};

// Register modules on import
registerQuillModules();

/**
 * Global ReactMailQuill Component
 * 
 * A wrapper around ReactQuill that handles all Quill module registrations
 * and provides consistent configuration across the mail client.
 * 
 * @param {string} value - The HTML content value
 * @param {function} onChange - Callback function when content changes (receives HTML string)
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 * @param {object} modules - Custom modules configuration (will be merged with defaults)
 * @param {array} formats - Custom formats array (will be merged with defaults)
 * @param {object} toolbarHandlers - Custom toolbar handlers (will be merged with defaults)
 * @param {string|array|object} toolbar - Custom toolbar configuration
 * @param {string} toolbarContainerId - ID of the toolbar container element (for custom toolbar)
 * @param {function} onRef - Callback to get the ReactQuill ref (deprecated, use ref instead)
 * @param {boolean} readOnly - Whether the editor is read-only
 * @param {object} quillProps - Additional props to pass to ReactQuill
 */
export const ReactMailQuill = forwardRef(({
  value = '',
  onChange,
  placeholder = 'Type your message here...',
  className = '',
  modules: customModules = null,
  formats: customFormats = null,
  toolbarHandlers: customToolbarHandlers = null,
  toolbar: customToolbar = null,
  toolbarContainerId = null,
  onRef = null,
  readOnly = false,
  quillProps = {},
}, ref) => {
  const quillRef = useRef(null);

  // Default toolbar handlers
  const defaultToolbarHandlers = useMemo(() => {
    const linkHandler = () => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      if (range) {
        const url = prompt('Enter URL:');
        if (url) {
          quill.formatText(range, 'link', url);
        }
      }
    };

    const boldHandler = () => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      if (range) {
        const format = quill.getFormat(range);
        quill.formatText(range, 'bold', !format.bold);
      } else {
        quill.format('bold', !quill.getFormat().bold);
      }
    };

    const italicHandler = () => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      if (range) {
        const format = quill.getFormat(range);
        quill.formatText(range, 'italic', !format.italic);
      } else {
        quill.format('italic', !quill.getFormat().italic);
      }
    };

    const underlineHandler = () => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      if (range) {
        const format = quill.getFormat(range);
        quill.formatText(range, 'underline', !format.underline);
      } else {
        quill.format('underline', !quill.getFormat().underline);
      }
    };

    return {
      link: linkHandler,
      bold: boldHandler,
      italic: italicHandler,
      underline: underlineHandler,
      ...customToolbarHandlers,
    };
  }, [customToolbarHandlers]);

  // Default modules configuration
  const defaultModules = useMemo(() => {
    const baseModules = {
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true,
      },
      imageUploader: {
        upload: (file) => {
          return new Promise((resolve, reject) => {
            // Create a loading placeholder image (spinner SVG as data URL)
            const loadingPlaceholder = "data:image/svg+xml," + encodeURIComponent(`
              <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
                <rect width="200" height="200" fill="#f3f4f6" rx="4"/>
                <g transform="translate(100, 100)">
                  <circle cx="0" cy="0" r="20" fill="none" stroke="#623ce7" stroke-width="3" stroke-dasharray="31.416" stroke-dashoffset="31.416" opacity="0.3">
                    <animate attributeName="stroke-dashoffset" values="31.416;0;31.416" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="0" cy="0" r="20" fill="none" stroke="#623ce7" stroke-width="3" stroke-dasharray="31.416" stroke-dashoffset="15.708">
                    <animate attributeName="stroke-dashoffset" values="15.708;47.124;15.708" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                </g>
                <text x="100" y="150" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" fill="#6b7280">Uploading...</text>
              </svg>
            `);
            
            const quill = quillRef.current?.getEditor();
            
            // quill-image-uploader inserts a placeholder before calling this function
            // Find the most recently inserted image and replace it with our loading placeholder
            if (quill) {
              // Use setTimeout to ensure quill-image-uploader's placeholder is inserted first
              setTimeout(() => {
                const editorElement = quill.container.querySelector('.ql-editor');
                if (editorElement) {
                  const images = editorElement.querySelectorAll('img');
                  if (images.length > 0) {
                    const lastImage = images[images.length - 1];
                    // Check if it's a placeholder (data URL or blob URL)
                    if (lastImage.src.startsWith('data:') || lastImage.src.startsWith('blob:')) {
                      lastImage.src = loadingPlaceholder;
                      lastImage.setAttribute('data-uploading', 'true');
                    }
                  }
                }
              }, 10);
            }
            
            // Upload the file
            mailClientAPI.uploadFile(file)
              .then((response) => {
                const fileData = response.Output || response;
                const imageUrl = getAssetLink("cdn", fileData);
                
                // The promise resolve will cause quill-image-uploader to replace the placeholder
                // But we can also manually replace it to ensure it happens
                if (quill) {
                  setTimeout(() => {
                    const editorElement = quill.container.querySelector('.ql-editor');
                    if (editorElement) {
                      const images = editorElement.querySelectorAll('img[data-uploading="true"]');
                      images.forEach(img => {
                        img.src = imageUrl;
                        img.removeAttribute('data-uploading');
                      });
                    }
                  }, 0);
                }
                
                resolve(imageUrl);
              })
              .catch((error) => {
                // Remove placeholder on error
                if (quill) {
                  setTimeout(() => {
                    const editorElement = quill.container.querySelector('.ql-editor');
                    if (editorElement) {
                      const images = editorElement.querySelectorAll('img[data-uploading="true"]');
                      images.forEach(img => img.remove());
                    }
                  }, 0);
                }
                
                reject("Upload failed");
                console.error("Error uploading image:", error);
              });
          });
        },
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize"],
      },
    };

    // Merge with custom modules
    if (customModules) {
      const mergedModules = {
        ...baseModules,
        ...customModules,
      };

      // Handle toolbar merging
      if (customModules.toolbar) {
        // If customModules has toolbar, merge it with base toolbar
        mergedModules.toolbar = {
          container: customModules.toolbar.container || toolbarContainerId || "#rich-area-text-effect",
          handlers: {
            ...defaultToolbarHandlers,
            ...(customModules.toolbar.handlers || {}),
          },
        };
      } else if (toolbarContainerId || customToolbar) {
        // If toolbarContainerId or customToolbar is provided but customModules doesn't have toolbar
        mergedModules.toolbar = customToolbar || {
          container: toolbarContainerId || "#rich-area-text-effect",
          handlers: defaultToolbarHandlers,
        };
      }

      return mergedModules;
    }

    // If custom toolbar is provided, use it
    if (customToolbar || toolbarContainerId) {
      baseModules.toolbar = customToolbar || {
        container: toolbarContainerId || "#rich-area-text-effect",
        handlers: defaultToolbarHandlers,
      };
    }

    return baseModules;
  }, [customModules, customToolbar, toolbarContainerId, defaultToolbarHandlers]);

  // Default formats
  const defaultFormats = [
    "bold",
    "italic",
    "underline",
    "color",
    "font",
    "size",
    "align",
    "list",
    "bullet",
    "blockquote",
    "link",
    "image",
  ];

  const formats = customFormats || defaultFormats;

  // Handle ref callback - support both forwardRef and onRef callback
  const handleRef = useCallback((el) => {
    if (el) {
      quillRef.current = el;
      // Support forwardRef
      if (ref) {
        if (typeof ref === 'function') {
          ref(el);
        } else {
          ref.current = el;
        }
      }
      // Support legacy onRef callback
      if (onRef) {
        onRef(el);
      }
    }
  }, [ref, onRef]);

  return (
    <ReactQuill
      ref={handleRef}
      theme="snow"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      modules={defaultModules}
      formats={formats}
      className={className}
      {...quillProps}
    />
  );
});

ReactMailQuill.displayName = 'ReactMailQuill';

export default ReactMailQuill;

